import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play, Sparkles, CheckCircle } from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1557682250-33bd709cbe85";

const stats = [
  // { value: "500+", label: "Projects Delivered" },
  // { value: "150+", label: "Happy Clients" },
  { value: "1+", label: "Years Experience" },
  { value: "99%", label: "Client Satisfaction" },
];

const highlights = [
  "SEO-friendly website architecture",
  "Conversion-focused design strategy",
  "Dedicated technical support",
];

export const HeroSection = () => {
  return (
    <section className="relative min-h-[88vh] sm:min-h-[90vh] flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-foreground/95 via-foreground/90 to-foreground/80" />

        {/* Animated Gradient Orbs  */}
        <motion.div
          className="hidden sm:block absolute top-16 right-10 md:top-20 md:right-20 w-64 h-64 md:w-96 md:h-96 rounded-full opacity-30"
          style={{
            background:
              "radial-gradient(circle, hsl(var(--primary)) 0%, transparent 70%)",
          }}
          animate={{ scale: [1, 1.2, 1], x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="hidden sm:block absolute bottom-16 left-10 md:bottom-20 md:left-20 w-52 h-52 md:w-72 md:h-72 rounded-full opacity-20"
          style={{
            background:
              "radial-gradient(circle, hsl(var(--secondary)) 0%, transparent 70%)",
          }}
          animate={{ scale: [1, 1.3, 1], x: [0, -20, 0], y: [0, 30, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="container-custom relative z-10 py-14 sm:py-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-primary/20 text-primary-foreground border border-primary/30 mb-6 sm:mb-8"
            >
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-medium">
                Your Digital Success Partner
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-[32px] leading-tight sm:text-5xl lg:text-6xl font-display font-bold text-background mb-4 sm:mb-6"
            >
              Digital Agency Vancouver Focused on{" "}
              <span className="gradient-text">Website Development & SEO</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base sm:text-lg text-background/70 mb-6 sm:mb-8 max-w-xl leading-relaxed mx-auto lg:mx-0"
            >
              Square Trigger helps businesses build high-performing websites and
              implement strategic SEO that drives measurable growth across
              Vancouver and throughout Canada.
            </motion.p>

           {/* Highlights */}
<motion.div
  initial={{ opacity: 0, y: 26 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6, delay: 0.3 }}
  className="mb-8 sm:mb-10"
>
  <div className="mx-auto lg:mx-0 max-w-sm">
    <ul className="space-y-3">
      {highlights.map((highlight, index) => (
        <li key={index} className="flex items-start gap-3 text-background/80">
          <CheckCircle className="h-5 w-5 text-secondary shrink-0 mt-[2px]" />
          <span className="text-sm leading-relaxed">{highlight}</span>
        </li>
      ))}
    </ul>
  </div>
</motion.div>

            {/* CTAs  */}
            <motion.div
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center justify-center lg:justify-start"
            >
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link to="/get-started" className="flex items-center justify-center gap-2">
                  Get Started Today
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="text-background border-background/30 hover:bg-background/10 w-full sm:w-auto justify-center"
              >
                <Link
                  to="/portfolio/case-studies"
                  className="flex items-center justify-center gap-2"
                >
                  <Play className="h-5 w-5" />
                  View Our Work
                </Link>
              </Button>
            </motion.div>
          </div>

          {/* Stats Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="relative"
          >
            <div className="glass-dark rounded-2xl p-5 sm:p-8 border border-background/10">
              <div className="grid grid-cols-2 gap-4 sm:gap-8">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.45,
                      delay: 0.45 + index * 0.08,
                    }}
                    className="text-center"
                  >
                    <div className="text-2xl sm:text-3xl md:text-4xl font-display font-bold gradient-text mb-1 sm:mb-2">
                      {stat.value}
                    </div>
                    <div className="text-xs sm:text-sm text-background/60">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Trust Badges (smaller on mobile) */}
              <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-background/10">
                <p className="text-center text-xs sm:text-sm text-background/50 mb-3 sm:mb-4">
                  Trusted by leading brands
                </p>
                <div className="flex justify-center gap-4 sm:gap-8 opacity-50 flex-wrap">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-14 h-7 sm:w-16 sm:h-8 bg-background/20 rounded"
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Decorative Elements  */}
            <div className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 w-20 h-20 sm:w-24 sm:h-24 gradient-primary rounded-2xl opacity-40 sm:opacity-50 blur-xl"></div>
            <div className="absolute -bottom-3 -left-3 sm:-bottom-4 sm:-left-4 w-24 h-24 sm:w-32 sm:h-32 bg-secondary rounded-full opacity-25 sm:opacity-30 blur-xl"></div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="hidden sm:block absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-2 text-background/50"
        >
          <span className="text-xs">Scroll to explore</span>
          <div className="w-6 h-10 border-2 border-background/30 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-background/50 rounded-full"
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};